import { useCallback, useEffect, useState } from "react";
import { updateInbar12Graph } from "../../../api/inbar12";
import {
    cancelInterpretation,
    confirmInterpretation,
} from "../../../api/interpretation";
import { getSmoothingRate } from "../../../api/smoothing";
import GraphsGridPageLayout from "../../../components/interpretation/GraphsGrid/GraphsGridPageLayout";
import { GraphsProvider } from "../../../context/GraphsProvider";
import { useInterpretationContext } from "../../../context/InterpretationContext";
import useInbar12Data from "../../../hooks/useInbar12Data";
import usePrompt from "../../../hooks/usePrompt";
import useTestId from "../../../hooks/useTestId";
import { GraphSystems, TestGraph } from "../../../types";
import useTestData from "../../../hooks/useTestData";
import _ from "lodash";
import { clearGraphsCache } from "../../../hooks/useGraphsFactory";

export default function Inbar12() {
    const { mutate: mutateTestData } = useTestData();

    const [systemIdToShow, setSystemIdToShow] = useState<
        GraphSystems | undefined
    >();
    const [historicTestsToShow, setHistoricTestsToShow] = useState<number[]>(
        []
    );
    const [smoothingValue, setSmoothingValue] = useState(50);
    const [isEnabled, setIsEnabled] = useState(false);

    const { isInterpretationDirty } = useInterpretationContext();
    const testId = useTestId();
    const graphValues = useInbar12Data({
        systemId: systemIdToShow,
        historicTestsToShow,
        enabled: isEnabled,
    });
    const {
        setOriginalGraphsValue,
        getGlobalUnsavedData,
        mutate,
        processChangesForCancel,
    } = graphValues;
    const globalUnsavedData = getGlobalUnsavedData();

    const { nextPathname } = usePrompt({
        isDirty: !_.isEmpty(globalUnsavedData),
        isInterpretationDirty,
        onConfirm: () => createConfirmFunction("confirm")(),
        onCancel: () => createConfirmFunction("cancel")(),
    });

    const createConfirmFunction = useCallback(
        (action: "confirm" | "cancel") => {
            return async () => {
                const promises: Promise<TestGraph>[] = [];
                const dataToProcess =
                    action === "cancel"
                        ? processChangesForCancel(globalUnsavedData)
                        : globalUnsavedData;

                dataToProcess?.forEach((item) => {
                    const payloadData = { ...item };
                    if (_.isEmpty(payloadData.changes)) {
                        return;
                    }
                    promises.push(
                        updateInbar12Graph({
                            testId,
                            graph_id: payloadData.graph_id,
                            ...payloadData.changes,
                        })
                    );
                });
                await Promise.all(promises);

                if (
                    isInterpretationDirty &&
                    !nextPathname.includes("interpretation") &&
                    testId
                ) {
                    if (action === "cancel") {
                        await cancelInterpretation(testId);
                    } else if (action === "confirm") {
                        await confirmInterpretation(testId);
                    }
                }
                await clearGraphsCache();
                const newGraphs = await mutate();

                setOriginalGraphsValue(newGraphs || null);
                mutateTestData();
            };
        },
        [testId, isInterpretationDirty, nextPathname]
    );

    useEffect(() => {
        if (testId) {
            getSmoothingRate(testId).then((data) => {
                setSmoothingValue(data.inbar12);
                setIsEnabled(true);
            });
        }
    }, [testId]);

    return (
        <GraphsProvider
            contextValue={{ ...graphValues, graphsType: "inbar-12" }}
        >
            <GraphsGridPageLayout
                title="Inbar 12 Panel"
                systemIdToShow={systemIdToShow}
                setSystemIdToShow={setSystemIdToShow}
                historicTestsToShow={historicTestsToShow}
                setHistoricTestsToShow={setHistoricTestsToShow}
                smoothingValue={smoothingValue}
                setSmoothingValue={setSmoothingValue}
            />
        </GraphsProvider>
    );
}
